import { COLLECTION_STATES, PER_OPTIONS, collectionOf, eventCostTotal, eventIncome, eventProfit, lineMultiplier, lineTotal, needsQuote } from "../lib/calc.js";
import { formatCurrency, formatDate, formatTimeRange, toLocalISODate } from "../lib/format.js";
import { escapeHtml, openModal } from "../lib/ui.js";

// Ficha de sólo lectura de un evento. Sirve para mirar sin riesgo de tocar:
// desde la lista y desde el calendario se llega acá, y recién desde acá se
// pasa a editar. Muestra la cuenta completa, no sólo el resultado.

const ESTADOS = {
  auto: ["Automático", "Los números salieron del paquete. Nadie los tocó."],
  ajustado: ["Ajustado", "La familia revisó y cambió algo respecto del paquete."],
};

const PER_LABEL = Object.fromEntries(PER_OPTIONS.map(([per, label]) => [per, label]));

/** Días entre la venta y el evento. Null si no se sabe cuándo se vendió. */
export function leadTimeDays(event) {
  if (!event?.sale_date || !event?.date) return null;
  const ms = new Date(`${event.date}T00:00:00`) - new Date(`${event.sale_date}T00:00:00`);
  return Math.round(ms / 86400000);
}

function lineRows(lines, withCounts, emptyLabel) {
  if (lines.length === 0) {
    return `<tr><th scope="row">${emptyLabel}</th><td class="calc"></td><td class="amount">—</td></tr>`;
  }
  return lines
    .map((line) => {
      const veces = lineMultiplier(line, withCounts);
      const pendiente = line.quote && !(Number(line.amount) > 0);
      const cuenta = line.per && line.per !== "fijo" ? `${veces} × ${formatCurrency(line.amount)}` : "";
      return `<tr class="${pendiente ? "needs-quote" : ""}">
        <th scope="row">${escapeHtml(line.concept || "Sin concepto")}
          ${line.per && line.per !== "fijo" ? `<span class="per-tag">${escapeHtml(PER_LABEL[line.per] ?? line.per)}${line.included ? ` desde ${Number(line.included) + 1}` : ""}</span>` : ""}
          ${pendiente ? '<span class="per-tag quote">a presupuestar</span>' : ""}
        </th>
        <td class="calc">${pendiente ? "" : cuenta}</td>
        <td class="amount">${pendiente ? "—" : formatCurrency(lineTotal(line, withCounts))}</td>
      </tr>`;
    })
    .join("");
}

function breakdownHtml(event, finance) {
  const withCounts = { ...event, ...finance };
  const cost = eventCostTotal(withCounts);
  const income = eventIncome(withCounts);
  const profit = income - cost;
  const ratio = income > 0 ? profit / income : null;
  const costs = Array.isArray(finance.cost_lines) ? finance.cost_lines : [];
  const prices = Array.isArray(finance.price_lines) ? finance.price_lines : [];

  return `
    ${
      needsQuote(costs)
        ? `<p class="detail-alert"><strong>Falta el presupuesto</strong> de un costo marcado "a presupuestar". El costo total está incompleto.</p>`
        : ""
    }
    <div class="table-wrap">
    <table class="detail-table">
      <tbody>
        <tr class="section"><th scope="row" colspan="3">Precio</th></tr>
        ${lineRows(prices, withCounts, "Sin precio cargado")}
        <tr class="section"><th scope="row" colspan="3">Costos</th></tr>
        ${lineRows(costs, withCounts, "Sin costos cargados")}
      </tbody>
      <tfoot>
        <tr class="total">
          <th scope="row" colspan="2">Precio</th>
          <td class="amount">${formatCurrency(income)}</td>
        </tr>
        <tr class="total">
          <th scope="row" colspan="2">Costo total</th>
          <td class="amount">${formatCurrency(cost)}</td>
        </tr>
        <tr class="total">
          <th scope="row" colspan="2">Ganancia</th>
          <td class="amount ${profit >= 0 ? "positive" : "negative"}">
            ${formatCurrency(profit)}${ratio === null ? "" : ` · ${Math.round(ratio * 100)}%`}
          </td>
        </tr>
      </tfoot>
    </table>
    </div>`;
}

/** Cobranza a la vista: qué entró, qué falta y en qué estado está. */
function collectionHtml(event) {
  const c = collectionOf({ ...event, ...(event.finance ?? {}) }, toLocalISODate());
  const [label, hint] = COLLECTION_STATES[c.state];
  return `
    <div class="collection-box state-${c.state}">
      <div class="collection-head">
        <span class="badge-state ${c.state}">${label}</span>
        <span class="hint">${hint}</span>
      </div>
      <div class="collection-facts">
        <div><span class="label">Precio</span><strong>${formatCurrency(c.price)}</strong></div>
        <div><span class="label">Seña</span><strong>${formatCurrency(c.deposit)}</strong></div>
        <div><span class="label">Saldo</span><strong>${formatCurrency(c.balance)}</strong></div>
        <div><span class="label">${c.pending > 0 ? "Falta cobrar" : "Cobrado"}</span>
             <strong class="${c.pending > 0 ? "negative" : "positive"}">${formatCurrency(c.pending > 0 ? c.pending : c.collected)}</strong></div>
      </div>
    </div>`;
}

export function eventDetailHtml(event, { products = [], packages = [] } = {}) {
  const product = products.find((p) => p.id === event.product_id) ?? null;
  const pack = packages.find((p) => p.id === event.package_id) ?? null;
  const finance = event.finance ?? null;
  const [estadoLabel, estadoHint] = ESTADOS[finance?.source] ?? [];

  const details = event.details && typeof event.details === "object" ? event.details : {};
  const schema = Array.isArray(product?.form_schema) ? product.form_schema : [];
  const ownFields = schema
    .map((f) => {
      const raw = details[f.name];
      if (raw === undefined || raw === "" || raw === false) return null;
      const value = raw === true ? "Sí" : String(raw);
      return `<div><dt>${escapeHtml(f.label)}</dt><dd>${escapeHtml(value)}</dd></div>`;
    })
    .filter(Boolean)
    .join("");

  return `
    <div class="event-detail">
      <div class="detail-head">
        <h3>${escapeHtml(event.description)}</h3>
        ${
          product
            ? `<span class="badge" style="background:${escapeHtml(product.color)}">${escapeHtml(product.name)}</span>`
            : ""
        }
      </div>

      <dl class="detail-facts">
        <div><dt>Fecha</dt><dd>${formatDate(event.date)}${
          event.start_time ? ` · ${formatTimeRange(event.start_time, event.end_time)}` : ""
        }</dd></div>
        ${
          event.sale_date
            ? `<div><dt>Vendido</dt><dd>${formatDate(event.sale_date)}
                 <span class="per-tag">${leadTimeDays(event)} días antes</span></dd></div>`
            : ""
        }
        <div><dt>Paquete</dt><dd>${pack ? escapeHtml(pack.name) : "sin paquete"}</dd></div>
        <div><dt>Chicos</dt><dd>${event.children_count ?? 0}</dd></div>
        <div><dt>Profesores</dt><dd>${event.teachers_count ?? 0}</dd></div>
        <div><dt>Souvenirs</dt><dd>${event.souvenirs_count ?? 0}</dd></div>
        ${ownFields}
      </dl>

      ${
        // Seguridad alimentaria: es lo único de esta pantalla que puede
        // lastimar a alguien si se pasa por alto. Va destacado, no como un
        // dato más de la lista.
        event.allergies
          ? `<p class="detail-alert"><strong>Alergias:</strong> ${escapeHtml(event.allergies)}</p>`
          : ""
      }

      ${
        event.child_name || event.contact || event.guardian_name || event.email
          ? `<dl class="detail-facts">
              ${event.child_name ? `<div><dt>Cumpleañero</dt><dd>${escapeHtml(event.child_name)}${event.child_age ? ` · ${event.child_age} años` : ""}</dd></div>` : ""}
              ${event.contact ? `<div><dt>Contacto</dt><dd>${escapeHtml(event.contact)}</dd></div>` : ""}
              ${event.guardian_name ? `<div><dt>Madre / padre</dt><dd>${escapeHtml(event.guardian_name)}${event.guardian_contact ? ` · ${escapeHtml(event.guardian_contact)}` : ""}</dd></div>` : ""}
              ${event.email ? `<div><dt>Email</dt><dd>${escapeHtml(event.email)}</dd></div>` : ""}
              ${event.assigned_coordinator ? `<div><dt>Coordina</dt><dd>${escapeHtml(event.assigned_coordinator)}</dd></div>` : ""}
            </dl>`
          : ""
      }

      ${event.notes ? `<p class="detail-notes">${escapeHtml(event.notes)}</p>` : ""}

      ${finance ? collectionHtml(event) : ""}

      ${
        finance
          ? `<div class="detail-money">
              <div class="detail-state">
                <span class="badge-state ${escapeHtml(finance.source ?? "auto")}">${escapeHtml(estadoLabel ?? "")}</span>
                <span class="hint">${escapeHtml(estadoHint ?? "")}</span>
              </div>
              ${breakdownHtml(event, finance)}
            </div>`
          : `<p class="hint">Todavía sin números. Lo cargó alguien sin paquete, así que hay que
             completarlos a mano desde Editar.</p>`
      }
    </div>`;
}

/** Abre la ficha en un modal. `onEdit` recibe el evento y decide qué hacer. */
export function openEventDetail(event, context, { onEdit } = {}) {
  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    ${eventDetailHtml(event, context)}
    <div class="modal-actions">
      <button type="button" class="btn secondary" data-close>Cerrar</button>
      ${onEdit ? '<button type="button" class="btn" id="detail-edit">Editar</button>' : ""}
    </div>
  `);

  modal.querySelectorAll("[data-close]").forEach((b) => b.addEventListener("click", () => modal.close()));
  modal.querySelector("#detail-edit")?.addEventListener("click", () => {
    modal.close();
    onEdit(event);
  });

  return modal;
}
